import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertCircle,
    ArrowDownRight,
    ArrowRight,
    ArrowUpRight,
    Check,
    CheckCircle2,
    Circle,
    Copy,
    ExternalLink,
    ImageOff,
    Lock,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { UiAction, UiBlock, Worded } from '../../../../common/hooks/use-assistant-api.hook';
import { actionKey, changeTone, stepKey } from './blocks';
import { ReplyChart } from './ReplyChart';
import { PreviewFor } from './Previews';

/**
 * Generative UI: every block a reply can carry, drawn from a fixed registry.
 *
 * The server decides WHAT (a checklist, a chart, cards) from data it read; this
 * decides only how each looks. Nothing a block offers happens by itself — an
 * `ask` is sent only when pressed, an `open` navigates only when pressed.
 */

export interface BlockHandlers {
    /** Send this as the vendor's next message. */
    onAsk: (message: string) => void;
    /** Go to a backoffice route. */
    onLink: (route: string) => void;
    /** While a turn is running, quick replies wait. */
    busy?: boolean;
}

/** An action's label and message, in the vendor's language when the server gave it an id. */
function useActionWords() {
    const { t } = useTranslation();
    return (action: UiAction) => {
        const labelKey = actionKey(action, 'LABEL');
        const messageKey = actionKey(action, 'MESSAGE');
        return {
            label: labelKey ? t(labelKey, { defaultValue: action.label }) : action.label,
            message: action.kind === 'ask' ? (messageKey ? t(messageKey, { defaultValue: action.message }) : action.message) : '',
        };
    };
}

/** A block's title: translated when the server gave it a key, the model's own words otherwise. */
function useTitle() {
    const { t } = useTranslation();
    return (block: { title?: string; titleKey?: string }) =>
        block.titleKey ? (t(`AGENTS.UI.TITLE.${block.titleKey}`, { defaultValue: block.title }) as string) : block.title;
}

/** A server-built detail, translated when it carries a key. */
function useDetail() {
    const { t } = useTranslation();
    return (w: Worded) => (w.detailKey ? t(`AGENTS.UI.DETAIL.${w.detailKey}`, { ...(w.params ?? {}), defaultValue: w.detail }) : w.detail);
}

const ActionButton: React.FC<{ action: UiAction; handlers: BlockHandlers; size?: 'sm' | 'xs'; primary?: boolean }> = ({
    action,
    handlers,
    primary,
}) => {
    const { t } = useTranslation();
    const words = useActionWords()(action);
    const [copied, setCopied] = useState(false);

    const press = () => {
        if (action.kind === 'ask') handlers.onAsk(words.message);
        else if (action.kind === 'open') handlers.onLink(action.route);
        else {
            void navigator.clipboard?.writeText(action.text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            });
        }
    };

    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn('h-7 px-2.5 text-xs', primary && 'border-foreground')}
            disabled={action.kind === 'ask' && handlers.busy}
            onClick={press}
        >
            {action.kind === 'copy' && (copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />)}
            {action.kind === 'copy' && copied ? t('AGENTS.UI.COPIED') : words.label}
            {action.kind === 'open' && <ArrowRight data-icon="inline-end" />}
        </Button>
    );
};

const Frame: React.FC<{ title?: string; aside?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
    title,
    aside,
    children,
    className,
}) => (
    <div className={cn('mt-2.5 rounded-xl border p-3', className)}>
        {(title || aside) && (
            <div className="mb-2 flex items-center gap-2">
                {title && <p className="text-xs font-medium text-muted-foreground">{title}</p>}
                {aside && <span className="ml-auto text-xs text-muted-foreground tabular-nums">{aside}</span>}
            </div>
        )}
        {children}
    </div>
);

const STEP_ICON = {
    done: <CheckCircle2 className="size-4 text-success" />,
    todo: <Circle className="size-4 text-muted-foreground" />,
    attention: <AlertCircle className="size-4 text-warning" />,
    blocked: <Lock className="size-3.5 text-muted-foreground/60" />,
} as const;

const Checklist: React.FC<{ block: Extract<UiBlock, { type: 'checklist' }>; handlers: BlockHandlers }> = ({ block, handlers }) => {
    const { t } = useTranslation();
    const detail = useDetail();
    const required = block.steps.filter((s) => !s.optional);
    const done = required.filter((s) => s.status === 'done').length;
    // The one step to do now gets the strong button; later ones stay quiet.
    const next = block.steps.find((s) => !s.optional && (s.status === 'todo' || s.status === 'attention'));
    const titleKey = block.goal ? `AGENTS.UI.GUIDE.${block.goal.toUpperCase()}.TITLE` : null;

    return (
        <Frame
            title={titleKey ? t(titleKey, { defaultValue: block.title }) : block.title}
            aside={t('AGENTS.UI.PROGRESS', { done, total: required.length })}
        >
            <div className="mb-2 h-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-foreground transition-all" style={{ width: `${required.length ? (done / required.length) * 100 : 0}%` }} />
            </div>
            <ol className="flex flex-col">
                {block.steps.map((s) => {
                    const labelKey = stepKey(block.goal, s.id, 'LABEL');
                    const said = detail(s);
                    return (
                        <li key={s.id} className={cn('flex gap-2.5 py-1.5', s.status === 'blocked' && 'opacity-60')}>
                            <span className="mt-0.5 shrink-0">{STEP_ICON[s.status]}</span>
                            <div className="min-w-0 flex-1">
                                <p className={cn('text-sm', s.status === 'done' && 'text-muted-foreground')}>
                                    {labelKey ? t(labelKey, { defaultValue: s.label }) : s.label}
                                    {s.optional && <span className="ml-1.5 text-xs text-muted-foreground">· {t('AGENTS.UI.OPTIONAL')}</span>}
                                </p>
                                {said && s.status !== 'done' && <p className="text-xs leading-snug text-muted-foreground">{said}</p>}
                                {said && s.status === 'done' && <p className="truncate text-xs text-muted-foreground">{said}</p>}
                                {s.action && s.status !== 'done' && s.status !== 'blocked' && (
                                    <div className="mt-1.5">
                                        <ActionButton action={s.action} handlers={handlers} primary={s === next} />
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </Frame>
    );
};

const CodeBlock: React.FC<{ block: Extract<UiBlock, { type: 'code' }> }> = ({ block }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const copy = () =>
        void navigator.clipboard?.writeText(block.code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });

    return (
        <Frame
            title={block.title ? t('AGENTS.UI.CODE.TITLE', { defaultValue: block.title }) : undefined}
            aside={
                <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={copy}>
                    {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                    {copied ? t('AGENTS.UI.COPIED') : t('AGENTS.UI.COPY')}
                </Button>
            }
        >
            <pre className="overflow-x-auto rounded-lg bg-muted px-3 py-2.5 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap break-all">
                {block.code}
            </pre>
            {block.note && <p className="mt-2 text-xs leading-snug text-muted-foreground">{t('AGENTS.UI.CODE.NOTE', { defaultValue: block.note })}</p>}
        </Frame>
    );
};

const Actions: React.FC<{ block: Extract<UiBlock, { type: 'actions' }>; handlers: BlockHandlers }> = ({ block, handlers }) => (
    <div className="mt-2.5">
        {block.prompt && <p className="mb-1.5 text-xs text-muted-foreground">{block.prompt}</p>}
        <div className="flex flex-wrap gap-1.5">
            {block.actions.map((a, i) => (
                <ActionButton key={`${a.label}-${i}`} action={a} handlers={handlers} />
            ))}
        </div>
    </div>
);

const TONE_TEXT = { good: 'text-success', risk: 'text-destructive', neutral: 'text-muted-foreground' } as const;

const Stats: React.FC<{ block: Extract<UiBlock, { type: 'stats' }> }> = ({ block }) => {
    const { t } = useTranslation();
    const title = useTitle();
    return (
    <Frame title={title(block)}>
        <div className={cn('grid gap-2', block.items.length === 1 ? 'grid-cols-1' : block.items.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
            {block.items.map((it) => {
                const tone = changeTone(it.change, it.tone);
                return (
                    <div key={it.label} className="rounded-lg bg-muted/50 px-2.5 py-2" title={it.hint}>
                        <p className="truncate text-[11px] text-muted-foreground">
                            {it.id ? t(`AGENTS.UI.METRIC.${it.id}`, { defaultValue: it.label }) : it.label}
                        </p>
                        <p className="text-lg leading-tight font-medium tabular-nums">{it.value}</p>
                        {it.change !== undefined && (
                            <p className={cn('inline-flex items-center gap-0.5 text-[11px] tabular-nums', TONE_TEXT[tone])}>
                                {it.change >= 0 ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
                                {Math.abs(it.change)}%
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
        {block.note && <p className="mt-2 text-xs text-muted-foreground">{block.note}</p>}
    </Frame>
    );
};

const TILE_DOT = {
    done: 'bg-success',
    attention: 'bg-warning',
    missing: 'bg-muted-foreground/40',
    locked: 'bg-muted-foreground/20',
} as const;

const Status: React.FC<{ block: Extract<UiBlock, { type: 'status' }>; handlers: BlockHandlers }> = ({ block, handlers }) => {
    const { t } = useTranslation();
    const detail = useDetail();
    return (
        <Frame title={block.title ? t('AGENTS.UI.STATUS.TITLE', { defaultValue: block.title }) : undefined}>
            <div className="grid grid-cols-2 gap-1.5">
                {block.items.map((it) => {
                    const said = detail(it);
                    return (
                        <div key={it.key} className="flex min-w-0 flex-col gap-1 rounded-lg border px-2.5 py-2">
                            <div className="flex items-center gap-1.5">
                                {it.state === 'locked' ? (
                                    <Lock className="size-3 text-muted-foreground/60" />
                                ) : (
                                    <span className={cn('size-2 shrink-0 rounded-full', TILE_DOT[it.state])} />
                                )}
                                <span className="truncate text-xs font-medium">{t(`AGENTS.UI.TILE.${it.key.toUpperCase()}`, { defaultValue: it.label })}</span>
                            </div>
                            <p className="truncate text-[11px] text-muted-foreground">{said || t(`AGENTS.UI.STATE.${it.state.toUpperCase()}`)}</p>
                            {it.action && it.state !== 'done' && (
                                <div>
                                    <ActionButton action={it.action} handlers={handlers} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Frame>
    );
};

const Cards: React.FC<{ block: Extract<UiBlock, { type: 'cards' }>; handlers: BlockHandlers }> = ({ block, handlers }) => {
    const { t } = useTranslation();
    const title = useTitle();
    return (
    <Frame title={title(block)}>
        <ul className="flex flex-col divide-y">
            {block.items.map((c, i) => (
                <li key={`${c.title}-${i}`} className="flex gap-2.5 py-2 first:pt-0 last:pb-0">
                    {c.image && <img src={c.image} alt="" loading="lazy" className="size-9 shrink-0 rounded-md border object-cover" />}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                            {c.href ? (
                                <a href={c.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-w-0 items-center gap-1 truncate text-sm font-medium hover:underline">
                                    <span className="truncate">{c.title}</span>
                                    <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
                                </a>
                            ) : (
                                <span className="truncate text-sm font-medium">{c.title}</span>
                            )}
                            {c.badge && (
                                <span className={cn('ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-medium bg-muted', c.tone && TONE_TEXT[c.tone])}>
                                    {c.badge}
                                </span>
                            )}
                        </div>
                        {c.subtitle && <p className="truncate text-xs text-muted-foreground">{c.subtitle}</p>}
                        {c.share !== undefined && (
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                                <div className="h-full rounded-full bg-foreground/80" style={{ width: `${Math.max(2, c.share * 100)}%` }} />
                            </div>
                        )}
                        {!!c.facts?.length && (
                            <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                {c.facts.map((f) => (
                                    <span key={f.label}>
                                        {f.id ? t(`AGENTS.UI.METRIC.${f.id}`, { defaultValue: f.label }) : f.label} <span className="font-medium text-foreground tabular-nums">{f.value}</span>
                                    </span>
                                ))}
                            </p>
                        )}
                        {c.action && (
                            <div className="mt-1.5">
                                <ActionButton action={c.action} handlers={handlers} />
                            </div>
                        )}
                    </div>
                </li>
            ))}
        </ul>
        {block.note && <p className="mt-2 text-xs text-muted-foreground">{block.note}</p>}
    </Frame>
    );
};

const Products: React.FC<{ block: Extract<UiBlock, { type: 'products' }>; handlers: BlockHandlers }> = ({ block, handlers }) => {
    const { t } = useTranslation();
    return (
        <Frame title={block.title}>
            <div className="grid grid-cols-2 gap-2">
                {block.items.map((p) => (
                    <button
                        key={p.uuid}
                        type="button"
                        onClick={() => handlers.onLink(`/products/${p.uuid}/edit`)}
                        className="group flex min-w-0 flex-col overflow-hidden rounded-lg border text-left transition-colors hover:border-foreground/40"
                    >
                        {p.image ? (
                            <img src={p.image} alt="" loading="lazy" className="aspect-[4/3] w-full bg-muted object-cover" />
                        ) : (
                            <span className="flex aspect-[4/3] w-full items-center justify-center bg-muted text-muted-foreground">
                                <ImageOff className="size-5" />
                            </span>
                        )}
                        <span className="flex flex-col gap-0.5 p-2">
                            <span className="truncate text-xs font-medium">{p.name}</span>
                            {p.sku && <span className="truncate text-[10.5px] text-muted-foreground">{p.sku}</span>}
                            <span className="mt-0.5 flex flex-wrap gap-1">
                                {p.ready === true && (
                                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">{t('AGENTS.UI.PRODUCT.READY')}</span>
                                )}
                                {p.ready === false && (
                                    <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning" title={p.missing?.join(' · ')}>
                                        {t('AGENTS.UI.PRODUCT.NOT_READY', { count: p.missing?.length ?? 0 })}
                                    </span>
                                )}
                                {p.visible === false && (
                                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{t('AGENTS.UI.PRODUCT.HIDDEN')}</span>
                                )}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
        </Frame>
    );
};

/** One block, drawn by its type. An unknown type draws nothing rather than guessing. */
export const ReplyBlock: React.FC<{ block: UiBlock; handlers: BlockHandlers }> = ({ block, handlers }) => {
    const { t } = useTranslation();
    switch (block.type) {
        case 'checklist':
            return <Checklist block={block} handlers={handlers} />;
        case 'code':
            return <CodeBlock block={block} />;
        case 'actions':
            return <Actions block={block} handlers={handlers} />;
        case 'stats':
            return <Stats block={block} />;
        case 'chart':
            return <ReplyChart block={block} />;
        case 'cards':
            return <Cards block={block} handlers={handlers} />;
        case 'products':
            return <Products block={block} handlers={handlers} />;
        case 'status':
            return <Status block={block} handlers={handlers} />;
        case 'preview':
            return (
                <PreviewFor
                    kind={block.kind}
                    settings={block.settings}
                    title={block.title ? t(`AGENTS.UI.PREVIEW.TITLE_${block.title.startsWith('Preview') ? 'TRY' : 'NOW'}`, { defaultValue: block.title }) : undefined}
                />
            );
        default:
            return null;
    }
};

/**
 * All of a reply's blocks, quick replies last — they are what the vendor does
 * after reading everything else.
 */
export const ReplyBlocks: React.FC<{ blocks?: UiBlock[]; handlers: BlockHandlers }> = ({ blocks, handlers }) => {
    if (!blocks?.length) return null;
    const ordered = [...blocks.filter((b) => b.type !== 'actions'), ...blocks.filter((b) => b.type === 'actions')];
    return (
        <>
            {ordered.map((b, i) => (
                <ReplyBlock key={`${b.type}-${i}`} block={b} handlers={handlers} />
            ))}
        </>
    );
};

export { ActionButton };
